import { amountMarkup } from './currency';
import { t, tp } from '../i18n/i18n';

/**
 * How many delvers you may keep, and how to keep one more.
 *
 * User: "3 players you can create by default. but all additional for 1000
 * ember."
 *
 * Three were already free and extra ones already had a price and an endpoint
 * — but nothing in the UI ever called it. A player who filled their third
 * slot got `no_free_slots` thrown by the creation wizard and no route out of
 * it, which made a built, paid feature invisible and the third delver feel
 * like a hard cap.
 *
 * ## What it says, and when
 *
 * The count is always shown, not only when full. "2 of 3 delvers" is the
 * thing that makes the fourth one's price make sense later; discovering the
 * limit at the moment you hit it is how a limit feels arbitrary.
 *
 * With a slot free, there is one button and it creates. With none free,
 * there is one button and it buys — never both, because two adjacent gold
 * buttons where one silently fails is worse than one honest one.
 *
 * Pure string builders, like the rest of `ui/` — the interesting states here
 * (free, full, full-and-broke, still loading) are all worth a test that does
 * not need a hub or a server.
 */
export interface SlotViewState {
  slots: { used: number; total: number; costEmber: number } | null;
  /** The player's Ember, for whether the purchase can actually go through. */
  ember: number;
  /** True while a purchase is in flight, so the button cannot be pressed twice. */
  busy?: boolean;
}

/** `2 of 3 delvers` — the word included, because a bare `2/3` names nothing. */
export function slotCountText(used: number, total: number): string {
  return tp(total, '{used} of {n} delver', '{used} of {n} delvers', { used });
}

export function characterSlotsMarkup(state: SlotViewState): string {
  const { slots, ember, busy } = state;

  // Before the count lands, offer the create button anyway: the server is the
  // authority on slots (Edge Case #2) and will refuse if it is wrong. A
  // disabled button during a fetch would be a worse guess than an optimistic one.
  if (!slots) {
    return `<button id="hub-character-add-btn" type="button" class="btn btn-gold px-4">${t('+ New Delver')}</button>`;
  }

  const count = `<p class="text-[12px] text-text-dim">${slotCountText(slots.used, slots.total)}</p>`;

  if (slots.used < slots.total) {
    return `
      <button id="hub-character-add-btn" type="button" class="btn btn-gold px-4">${t('+ New Delver')}</button>
      ${count}`;
  }

  const affordable = ember >= slots.costEmber;
  const price = amountMarkup('ember', slots.costEmber, { hideLabel: true, tone: affordable ? 'text-bg' : 'text-text-dim' });
  const button = affordable && !busy
    ? `<button id="hub-character-slot-buy" type="button" class="btn btn-gold px-4">
         <span class="inline-flex items-center gap-1">${busy ? t('Buying…') : t('Another slot')}${price}</span>
       </button>`
    : `<button type="button" class="btn btn-stone px-4" disabled aria-disabled="true">
         <span class="inline-flex items-center gap-1">${t('Another slot')}${price}</span>
       </button>`;

  return `
    ${button}
    ${count}
    <p class="text-[12px] ${affordable ? 'text-text-dim' : 'text-error'} basis-full">
      ${affordable
        ? t('Three come with the account. Extra slots are permanent and never expire.')
        : t('Not enough Ember for another slot — the Shop sells it.')}
    </p>`;
}
